import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard, EmailGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { FaSort, FaTrashAlt, FaAngleDoubleLeft, FaAngleLeft, FaAngleRight, FaAngleDoubleRight } from "react-icons/fa";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [originalUsers, setOriginalUsers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/users/allusers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.response && Array.isArray(response.data.response)) {
        setUsers(response.data.response);
        setOriginalUsers(response.data.response);
      } else {
        setUsers([]);
        setOriginalUsers([]);
      }
    } catch (err) {
      setError("Failed to fetch users.");
      toast.error(err.response?.data?.response || "Failed to fetch data.");
      console.error("Error fetching users:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }
    const token = localStorage.getItem("token");
    setIsLoading(true);
    try {
      await axios.delete(
        `${StandardURL}/iNyumba/users/${id}/delete`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("User deleted successfully");
      setUsers(prevUsers => prevUsers.filter(user => user.uuid !== id));
      setOriginalUsers(prevOriginalUsers => prevOriginalUsers.filter(user => user.uuid !== id));
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error(err.response?.data?.response || "Failed to delete user.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const filteredUsers = originalUsers.filter(user =>
      Object.values(user).some(value =>
        String(value).toLowerCase().includes(searchInput.toLowerCase())
      )
    );
    setUsers(filteredUsers);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleReset = () => {
    setSearchInput("");
    setUsers(originalUsers);
    setCurrentPage(1); // Reset to first page
  };

  const handleDownload = async (endpoint, fileType) => {
    const token = localStorage.getItem("token");
    setIsLoading(true);
    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { format: fileType },
        responseType: "blob",
      });
      const fileExtension = fileType === "pdf" ? "pdf" : "csv";
      const fileName = `usersreport_${new Date().toISOString()}.${fileExtension}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Download is successful");
    } catch (err) {
      console.error(`Error downloading ${fileType.toUpperCase()}:`, err);
      toast.error(err.response?.data?.response || `Error downloading ${fileType.toUpperCase()}`);
    } finally {
      setIsLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (sortConfig.key !== null) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Pagination UI rendering logic
  const pageNumbers = [];
  const maxPageNumbersToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1);
  if (endPage - startPage + 1 < maxPageNumbersToShow) {
    startPage = Math.max(1, endPage - maxPageNumbersToShow + 1);
  }
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Users</h1>

          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center mb-4 space-y-4 md:space-y-0 md:space-x-4">
              <input
                type="text"
                placeholder="Search by any field..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="border border-gray-300 rounded-md py-2 px-4 w-full md:w-1/2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex space-x-2 w-full md:w-auto">
                <button
                  onClick={handleSearch}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-150 ease-in-out w-1/2 md:w-auto"
                >
                  Search
                </button>
                <button
                  onClick={handleReset}
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md transition duration-150 ease-in-out w-1/2 md:w-auto"
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 justify-start mb-6">
              <div className="flex space-x-2 items-center">
                <span className="text-sm font-medium text-gray-700">Download Tenants:</span>
                <button
                  onClick={() => handleDownload("https://backend.inyumba.co.ke/iNyumba/tenantsreports", "csv")}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-3 rounded-md text-sm transition duration-150 ease-in-out flex items-center"
                >
                  <FontAwesomeIcon icon={faFileCsv} className="mr-1" /> CSV
                </button>
                <button
                  onClick={() => handleDownload("https://backend.inyumba.co.ke/iNyumba/tenantsreports", "pdf")}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-md text-sm transition duration-150 ease-in-out flex items-center"
                >
                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" /> PDF
                </button>
              </div>

              <div className="flex space-x-2 items-center">
                <span className="text-sm font-medium text-gray-700">Download Landlords:</span>
                <button
                  onClick={() => handleDownload("https://backend.inyumba.co.ke/iNyumba/landlordreports", "csv")}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-3 rounded-md text-sm transition duration-150 ease-in-out flex items-center"
                >
                  <FontAwesomeIcon icon={faFileCsv} className="mr-1" /> CSV
                </button>
                <button
                  onClick={() => handleDownload("https://backend.inyumba.co.ke/iNyumba/landlordreports", "pdf")}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-md text-sm transition duration-150 ease-in-out flex items-center"
                >
                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" /> PDF
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <Oval color="#4F46E5" height={50} width={50} />
              </div>
            ) : error ? (
              <div className="text-center p-8 text-red-500 font-semibold">
                {error}
              </div>
            ) : users.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                No users found.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {["id", "name", "email", "uuid", "phone", "role", "status", "login_attempt"].map(header => (
                          <th
                            key={header}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => requestSort(header)}
                          >
                            <div className="flex items-center">
                              {header.replace(/_/g, ' ').toUpperCase()}
                              <FaSort className="ml-1 text-gray-400" />
                              {getSortIcon(header)}
                            </div>
                          </th>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentItems.map(user => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.uuid}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.phone}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.role}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                              ${user.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.login_attempt}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => deleteUser(user.uuid)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete User"
                            >
                              <FaTrashAlt className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <nav className="flex items-center justify-center space-x-2 mt-6 p-4">
                    <button
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleDoubleLeft />
                    </button>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleLeft />
                    </button>
                    {pageNumbers.map(number => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-4 py-2 text-sm font-medium rounded-md border border-gray-300 transition-colors duration-150 ease-in-out
                          ${currentPage === number
                            ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                            : 'text-gray-700 bg-white hover:bg-gray-100'}`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleRight />
                    </button>
                    <button
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleDoubleRight />
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default UsersPage;